import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, setDoc, deleteDoc, writeBatch, onSnapshot, deleteField } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useModuleAdmin } from '../useModuleAdmin';
import { ACTOR_BUCKET_FOR, normaliseRole, isAdminRole } from '../roles';
import {
  Users, ListChecks, CalendarRange, LayoutList, Plus, Trash2, X, Upload, Download, Pencil, ChevronDown,
  Settings as SettingsIcon, BarChart3, Wand2,
} from 'lucide-react';
import Papa from 'papaparse';
import HeaderActionsPortal from './HeaderActionsPortal';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

const pad2 = (n) => String(n).padStart(2, '0');

// Buckets a raw profile role into one of the roles the Settings "Who Can Edit" matrix is keyed on.
// Mirrors Attendance.jsx's actorRoleBucket -- Admin is its own bucket (always granted, via the
// isAdmin safety net in canManage) rather than a role a checkbox can toggle. Student is also its own
// bucket (never a grantable checkbox) so a "Staff" grant can never accidentally cover students.
// Maps a canonical role onto the permission-checkbox labels, which are stored in customer data and
// so are not migrated. Anything absent from the map -- parent, denied -- matches no permission list,
// which is exactly the intent: they hold no duty permissions of any kind.
const actorRoleBucket = (rawRole) => ACTOR_BUCKET_FOR[normaliseRole(rawRole)] || 'Denied';

const addMinutes = (hhmm, mins) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const total = ((h * 60 + m + Number(mins || 0)) % 1440 + 1440) % 1440;
  return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
};

const timeToMinutes = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

// <input type="time"> requires a strictly zero-padded "HH:MM" value -- anything else (e.g. "8:30",
// which is what Excel exports for a Date/Time-formatted cell whose hour is a single digit) is silently
// rejected by the browser and renders as an empty "--:--" even though the underlying data is intact.
// CSV-imported times are normalized through this before being written, so imported single-digit-hour
// times actually show up in the editable time field instead of only in plain-text displays.
const normalizeTime = (hhmm) => {
  const parts = (hhmm || '').trim().split(':');
  if (parts.length < 2) return hhmm;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  return `${pad2(h)}:${pad2(m)}`;
};


// The Schedule/Allocation tabs' date columns render as a mini day-timetable so cards sit at their
// actual time and relative position, instead of just stacking in whatever order they were dropped.
// The visible range defaults to 08:00-17:00 but is overridden per-assessment by the Settings tab's
// Assessment Periods start/end time (falls back to these defaults when an assessment hasn't set them).
const TIMETABLE_START_MIN = 8 * 60;
const TIMETABLE_END_MIN = 17 * 60;
const PX_PER_MIN = 2;
// Each row is 30 minutes so a drop can be snapped to a clear, easy-to-target slot; the exact time is
// then fine-tuned with the card's own time input.
const TIMETABLE_SLOT_MIN = 30;

// Ruler marks, pixel height, and 30-minute gridlines for a given day's visible time range -- computed
// per-assessment instead of the old fixed 08:00-17:00 constants, since Settings can now narrow or
// widen that range per assessment.
const computeTimetableGeom = (startMin, endMin) => {
  const hourMarks = [];
  for (let m = startMin; m <= endMin; m += 60) hourMarks.push(m);
  const slotMarks = [];
  for (let m = startMin; m <= endMin; m += TIMETABLE_SLOT_MIN) slotMarks.push(m);
  return { height: (endMin - startMin) * PX_PER_MIN, hourMarks, slotMarks };
};

// Every date string (inclusive) between start and end, "YYYY-MM-DD" both ends.
const dateRange = (start, end) => {
  if (!start || !end) return [];
  const dates = [];
  const cur = new Date(`${start}T00:00:00`);
  const last = new Date(`${end}T00:00:00`);
  if (Number.isNaN(cur.getTime()) || Number.isNaN(last.getTime()) || cur > last) return [];
  while (cur <= last) {
    dates.push(`${cur.getFullYear()}-${pad2(cur.getMonth() + 1)}-${pad2(cur.getDate())}`);
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
};

// Unassigned (no date yet): Class Subject Paper / Duration / Invigilators. Once a session has a date
// (placed on a timetable): Class Subject Paper / Start Time (editable) / End Time / Venue /
// Invigilators. A grouped card (several classes sharing the same Subject+Paper+start time) instead
// shows: Subject Paper / Start-End time / every class it covers, each with its own venue chip and
// invigilator chips.
// onDropStaff/onRemoveStaff/onDropVenue/onRemoveVenue take (targetSession, value) -- for a grouped
// card, each class's own column is its own drop target so a chip assigns to that real underlying
// session, not the whole merged group (which has no invigilators/venue of its own to write to).
const SessionCard = ({ session, draggable, onDragStart, assignable, onDropStaff, onRemoveStaff, onDropVenue, onRemoveVenue, isInvigilatorClashing = () => false, timeEditable, onTimeChange }) => {
  const placed = !!session._draftDate;
  const isGroup = !!session._isGroup;
  const title = isGroup
    ? [session.subject, session.paper].filter(Boolean).join(' ')
    : [session.class, session.subject, session.paper].filter(Boolean).join(' ');
  const venueChip = (venue, onRemove) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.15rem', padding: '0.1rem 0.4rem', borderRadius: '999px', backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-primary)', color: 'var(--color-primary)', fontSize: '0.7rem', fontWeight: '600' }}>
      {venue}
      {assignable && (
        <button type="button" onClick={onRemove} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex', padding: 0 }}>
          <X size={9} />
        </button>
      )}
    </span>
  );
  return (
  <div
    draggable={draggable}
    onDragStart={draggable ? (e) => onDragStart(e, session.id) : undefined}
    onDragOver={(assignable && !isGroup) ? (e) => e.preventDefault() : undefined}
    onDrop={(assignable && !isGroup) ? (e) => {
      const staffInitial = e.dataTransfer.getData('application/x-staff-initial');
      const venueCode = e.dataTransfer.getData('application/x-venue-code');
      if (staffInitial) {
        e.stopPropagation();
        onDropStaff && onDropStaff(session, staffInitial);
      } else if (venueCode) {
        e.stopPropagation();
        onDropVenue && onDropVenue(session, venueCode);
      }
      // Not a staff/venue chip -- let it bubble up so the column's own onDrop can reposition the session.
    } : undefined}
    style={{
      backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-primary)', borderRadius: 'var(--radius-md)',
      padding: '0.6rem 0.75rem', cursor: draggable ? 'grab' : 'default', fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: '0.3rem',
      width: '100%', height: '100%', boxSizing: 'border-box', overflow: 'auto', boxShadow: 'var(--shadow-sm)',
    }}
  >
    <div style={{ fontWeight: '700' }}>{title}</div>
    {placed && isGroup ? (
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: 'var(--color-text-muted)' }}>
          {timeEditable ? (
            <input
              type="time"
              value={session._draftStartTime || ''}
              onChange={(e) => onTimeChange(session.id, e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              style={{ fontSize: '0.75rem', padding: '0.15rem', border: '1px solid var(--color-border)', borderRadius: '4px', width: '95px' }}
            />
          ) : (
            <span>{session._draftStartTime || '--:--'}</span>
          )}
          <span>-</span>
          <span>{session._draftStartTime ? addMinutes(session._draftStartTime, session.duration) : '--:--'}</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
          {session._members.map(member => (
            <div
              key={member.id}
              onDragOver={assignable ? (e) => e.preventDefault() : undefined}
              onDrop={assignable ? (e) => {
                const staffInitial = e.dataTransfer.getData('application/x-staff-initial');
                const venueCode = e.dataTransfer.getData('application/x-venue-code');
                if (staffInitial) {
                  e.stopPropagation();
                  onDropStaff && onDropStaff(member, staffInitial);
                } else if (venueCode) {
                  e.stopPropagation();
                  onDropVenue && onDropVenue(member, venueCode);
                }
              } : undefined}
              style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', alignItems: 'flex-start', minWidth: '28px' }}
            >
              <span style={{ color: 'var(--color-text-muted)', fontWeight: '600' }}>{member.class}</span>
              {member.venue && venueChip(member.venue, () => onRemoveVenue && onRemoveVenue(member))}
              {(member.invigilators || []).map(initial => {
                const clashing = isInvigilatorClashing(member.id, initial);
                return (
                  <span key={initial} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.15rem', padding: '0.1rem 0.4rem', borderRadius: '999px', backgroundColor: clashing ? 'var(--color-danger)' : 'var(--color-bg-app)', border: `1px solid ${clashing ? 'var(--color-danger)' : 'var(--color-border)'}`, color: clashing ? '#fff' : 'inherit', fontSize: '0.7rem', fontWeight: '600' }} title={clashing ? 'This person is already invigilating another session at an overlapping time.' : undefined}>
                    {initial}
                    {assignable && (
                      <button type="button" onClick={() => onRemoveStaff && onRemoveStaff(member, initial)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: clashing ? '#fff' : 'var(--color-danger)', display: 'flex', padding: 0 }}>
                        <X size={9} />
                      </button>
                    )}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      </>
    ) : placed ? (
      <>
        {timeEditable ? (
          <input
            type="time"
            value={session._draftStartTime || ''}
            onChange={(e) => onTimeChange(session.id, e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ fontSize: '0.75rem', padding: '0.15rem', border: '1px solid var(--color-border)', borderRadius: '4px', width: '95px' }}
          />
        ) : (
          <div style={{ color: 'var(--color-text-muted)' }}>{session._draftStartTime || '--:--'}</div>
        )}
        <div style={{ color: 'var(--color-text-muted)' }}>
          {session._draftStartTime ? addMinutes(session._draftStartTime, session.duration) : '--:--'}
        </div>
      </>
    ) : (
      <div style={{ color: 'var(--color-text-muted)' }}>{session.duration || 0} min</div>
    )}
    {!isGroup && session.venue && (
      <div>
        {venueChip(session.venue, () => onRemoveVenue && onRemoveVenue(session))}
      </div>
    )}
    {!isGroup && (session.invigilators || []).length > 0 && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: 'auto' }}>
        {session.invigilators.map(initial => {
          const clashing = isInvigilatorClashing(session.id, initial);
          return (
            <span key={initial} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', padding: '0.1rem 0.4rem', borderRadius: '999px', backgroundColor: clashing ? 'var(--color-danger)' : 'var(--color-bg-app)', border: `1px solid ${clashing ? 'var(--color-danger)' : 'var(--color-border)'}`, color: clashing ? '#fff' : 'inherit', fontSize: '0.7rem', fontWeight: '600' }} title={clashing ? 'This person is already invigilating another session at an overlapping time.' : undefined}>
              {initial}
              {assignable && (
                <button type="button" onClick={() => onRemoveStaff && onRemoveStaff(session, initial)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: clashing ? '#fff' : 'var(--color-danger)', display: 'flex', padding: 0 }}>
                  <X size={10} />
                </button>
              )}
            </span>
          );
        })}
      </div>
    )}
  </div>
  );
};

// Merges sessions that share Subject + Paper + start time into one card listing every class inside
// it, so N parallel classes of the same paper (all scheduled together, as is typical for a class
// level) don't each claim their own cramped side-by-side lane. Sessions with no siblings pass through
// unchanged (single-member "group" collapses back to a plain session via the length===1 check).
const groupSessionsForCard = (sessions) => {
  const groups = new Map();
  sessions.forEach(s => {
    const key = `${s.subject}||${s.paper || ''}||${s._draftStartTime || ''}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(s);
  });
  return [...groups.values()].map(members => {
    if (members.length === 1) return members[0];
    return {
      ...members[0],
      id: members.map(m => m.id).join(','),
      // Keeps the real member sessions (each with its own real id and invigilators array) so a staff
      // chip dropped on one class's column assigns to THAT session, not the whole merged group.
      _members: members,
      _isGroup: true,
    };
  });
};

// Greedy calendar-style lane assignment: sessions that overlap in time get placed in different lanes
// (side-by-side columns); non-overlapping sessions reuse a free lane once it's clear. Plain function
// (not a hook) so it can be called once per date/level while building the day/level sections below.
const computeDayLanes = (annotatedSessions, date, dayStartMin = TIMETABLE_START_MIN, group = false) => {
  let filtered = annotatedSessions.filter(s => s._draftDate === date);
  if (group) filtered = groupSessionsForCard(filtered);
  const daySessions = filtered
    .map(s => {
      const start = timeToMinutes(s._draftStartTime) ?? dayStartMin;
      return { ...s, _start: start, _end: start + (Number(s.duration) || 60) };
    })
    .sort((a, b) => a._start - b._start || a._end - b._end);

  const laneEndTimes = [];
  daySessions.forEach(s => {
    let laneIdx = laneEndTimes.findIndex(endTime => endTime <= s._start);
    if (laneIdx === -1) { laneIdx = laneEndTimes.length; laneEndTimes.push(s._end); }
    else { laneEndTimes[laneIdx] = s._end; }
    s._lane = laneIdx;
  });
  return { sessions: daySessions, laneCount: Math.max(1, laneEndTimes.length) };
};

// A row of timetable columns sharing one time ruler -- used for both the Schedule tab (one column per
// exam date, all for one class level) and the Allocation tab (one column per class level, all for one
// day). Each column collapses to a thin strip so a long list of columns can be skimmed at a glance.
const TimetableColumnsRow = ({
  entries, canManage, assignable, collapsedSections, onToggleSection, onColumnDragOver, onColumnDrop, onCardDragStart,
  onDropStaff = () => {}, onRemoveStaff = () => {}, onDropVenue = () => {}, onRemoveVenue = () => {}, onTimeChange = () => {},
  isInvigilatorClashing = () => false,
  startMin = TIMETABLE_START_MIN, endMin = TIMETABLE_END_MIN,
}) => {
  const { t } = useT();
  const { height, hourMarks, slotMarks } = computeTimetableGeom(startMin, endMin);
  return (
  <div style={{ overflowX: 'auto', paddingBottom: '0.5rem' }}>
    <div style={{ display: 'flex', gap: '0.75rem' }}>
      <div style={{ flex: '0 0 44px', position: 'sticky', left: 0, backgroundColor: 'var(--color-bg-app)', zIndex: 3 }}>
        <div style={{ height: '1.9rem' }} />
        <div style={{ position: 'relative', height }}>
          {hourMarks.map(m => (
            <div key={m} style={{ position: 'absolute', top: (m - startMin) * PX_PER_MIN, right: '0.4rem', transform: 'translateY(-50%)', fontSize: '0.7rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
              {pad2(Math.floor(m / 60))}:{pad2(m % 60)}
            </div>
          ))}
        </div>
      </div>

      {entries.map(({ key, label, lanes, dropDate }) => {
        const collapsed = !!collapsedSections[key];
        // A grouped card lists every member class side by side in one row (class label + venue chip +
        // invigilator chip each) -- widen the column so that row has enough room not to wrap, which is
        // what let a tall stack of chips force a scrollbar inside the card before.
        const maxGroupSize = Math.max(1, ...lanes.sessions.map(s => (s._isGroup ? s._members.length : 1)));
        const colWidth = Math.max(260, lanes.laneCount * 130, maxGroupSize * 70);
        // Expanded columns grow to share any leftover row width equally (so collapsing some columns
        // widens the rest, and columns with the same lane count end up the same width as each other),
        // but never shrink below their own content's minimum -- a narrow viewport scrolls horizontally
        // instead of squeezing cards unreadably thin. Collapsed columns stay fixed at a thin strip.
        return (
          <div key={key} style={{ flex: collapsed ? '0 0 36px' : `1 0 ${colWidth}px` }}>
            <button
              type="button" onClick={() => onToggleSection(key)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem 0', height: '1.9rem', whiteSpace: 'nowrap', overflow: 'hidden' }}
            >
              <ChevronDown size={14} style={{ transform: collapsed ? 'rotate(-90deg)' : 'none', color: 'var(--color-text-muted)', flexShrink: 0 }} />
              {!collapsed && <span style={{ fontWeight: '700', fontSize: '0.8rem' }}>{label}</span>}
            </button>
            {collapsed ? (
              <div
                onClick={() => onToggleSection(key)} title={`${label} -- ${lanes.sessions.length} session${lanes.sessions.length === 1 ? '' : 's'}`}
                style={{ cursor: 'pointer', height, borderRadius: 'var(--radius-md)', border: '1px dashed var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <span style={{ writingMode: 'vertical-rl', fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>{label} ({lanes.sessions.length})</span>
              </div>
            ) : (
              <div
                onDragOver={canManage ? onColumnDragOver : undefined}
                onDrop={canManage ? (e) => onColumnDrop(e, dropDate) : undefined}
                style={{ position: 'relative', height, backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-sm)' }}
              >
                {slotMarks.map(m => (
                  <div key={m} style={{ position: 'absolute', top: (m - startMin) * PX_PER_MIN, left: 0, right: 0, borderTop: `1px ${m % 60 === 0 ? 'solid' : 'dashed'} var(--color-border)` }} />
                ))}
                {lanes.sessions.length === 0 && (
                  <div style={{ position: 'absolute', top: '0.5rem', left: '0.5rem', fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>{t('invigilation.noSessions')}</div>
                )}
                {lanes.sessions.map(s => {
                  const top = Math.min(Math.max(0, (s._start - startMin) * PX_PER_MIN), height - 10);
                  // A grouped card needs room for the time row plus one row of class/venue/invigilator
                  // chips beneath it -- with the column now wide enough that row shouldn't wrap (see
                  // colWidth above), so a single, higher floor covers it instead of a per-card scrollbar.
                  const cardHeight = Math.max((s.duration || 60) * PX_PER_MIN, s._isGroup ? 170 : 112);
                  const laneWidthPct = 100 / lanes.laneCount;
                  const leftPct = s._lane * laneWidthPct;
                  return (
                    <div key={s.id} style={{ position: 'absolute', top, height: cardHeight, left: `calc(${leftPct}% + 3px)`, width: `calc(${laneWidthPct}% - 6px)`, zIndex: 2 }}>
                      <SessionCard
                        session={s} draggable={canManage} onDragStart={onCardDragStart}
                        assignable={assignable} onDropStaff={onDropStaff} onRemoveStaff={onRemoveStaff}
                        onDropVenue={onDropVenue} onRemoveVenue={onRemoveVenue}
                        isInvigilatorClashing={isInvigilatorClashing}
                        timeEditable={canManage} onTimeChange={onTimeChange}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
  );
};

// Sticky sidebar of draggable staff initials, dropped onto a SessionCard to assign that person.
const StaffPanel = ({ staffList, onStaffDragStart, collapsed, onToggle }) => {
  const { t } = useT();
  return (
  <div style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', padding: '0.85rem' }}>
    <button
      type="button" onClick={onToggle}
      style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: collapsed ? 0 : '0.6rem' }}
    >
      <ChevronDown size={14} style={{ transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.15s', color: 'var(--color-text-muted)', flexShrink: 0 }} />
      <span style={{ fontSize: '0.8rem', fontWeight: '700' }}>{t('invigilation.staff')}</span>
    </button>
    {!collapsed && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
        {staffList.map(st => (
          <div
            key={st.initial} draggable onDragStart={(e) => onStaffDragStart(e, st.initial)} title={st.name}
            style={{ cursor: 'grab', padding: '0.3rem 0.65rem', borderRadius: '999px', border: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-app)', fontSize: '0.75rem', fontWeight: '600' }}
          >
            {st.initial}
          </div>
        ))}
      </div>
    )}
  </div>
  );
};

// Same drag-source pattern as StaffPanel, styled distinctly (primary-colored outline) so a venue chip
// reads differently from a staff chip while dragging.
const VenuePanel = ({ venues, onVenueDragStart, collapsed, onToggle }) => {
  const { t } = useT();
  return (
  <div style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', padding: '0.85rem' }}>
    <button
      type="button" onClick={onToggle}
      style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: collapsed ? 0 : '0.6rem' }}
    >
      <ChevronDown size={14} style={{ transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.15s', color: 'var(--color-text-muted)', flexShrink: 0 }} />
      <span style={{ fontSize: '0.8rem', fontWeight: '700' }}>{t('invigilation.venues')}</span>
    </button>
    {!collapsed && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
        {venues.length === 0 ? (
          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('invigilation.noVenuesAvailable')}</span>
        ) : venues.map(v => (
          <div
            key={v.code} draggable onDragStart={(e) => onVenueDragStart(e, v.code)} title={v.nameEn || v.code}
            style={{ cursor: 'grab', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.3rem 0.65rem', borderRadius: '999px', border: '1px solid var(--color-primary)', color: 'var(--color-primary)', backgroundColor: 'var(--color-bg-app)', fontSize: '0.75rem', fontWeight: '600' }}
          >
            {v.code}
          </div>
        ))}
      </div>
    )}
  </div>
  );
};

const Invigilation = () => {
  const { t } = useT();
  const { activeSchoolId, userProfile, isMobile } = useOutletContext();
  const [activeTab, setActiveTab] = useState('invigilation');

  const ownRole = (userProfile?.role || '').toLowerCase();
  const isAdmin = isAdminRole(ownRole);
  const ownEmail = (auth.currentUser?.email || '').toLowerCase();
  const ownActorRole = actorRoleBucket(ownRole);
  // Named admins of this module: the one list the rules read, replacing the per-action
  // staff pickers that used to live in this module's own settings document.
  const isModuleAdmin = useModuleAdmin(activeSchoolId, 'invigilation');

  const [academicYears, setAcademicYears] = useState([]);
  const [schoolYear, setSchoolYear] = useState('');
  const [assessments, setAssessments] = useState([]);
  const [assessment, setAssessment] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [staffList, setStaffList] = useState([]);

  useEffect(() => {
    if (!activeSchoolId) return;
    (async () => {
      try {
        const ySnap = await getDocs(collection(db, 'schools', activeSchoolId, 'academic_years'));
        const yList = [];
        ySnap.forEach(d => yList.push({ uid: d.id, ...d.data() }));
        yList.sort((a, b) => (a.yearId || '').localeCompare(b.yearId || ''));
        setAcademicYears(yList);
        if (yList.length > 0) {
          const cur = yList.find(y => y.cur_year) || yList[yList.length - 1];
          setSchoolYear(cur.yearId);
        }
      } catch (e) { console.error('Error loading academic years', e); }

      try {
        const sysSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings'));
        const dropdowns = sysSnap.exists() ? (sysSnap.data().dropdowns || {}) : {};
        // Only assessments actually sat as a written test/exam need invigilation sessions --
        // computed aggregates like "Term 1" or "Annual" have no exam of their own to invigilate.
        const assessList = (dropdowns.assessments || []).filter(a => a.active !== false && a.writtenTest !== false);
        setAssessments(assessList);
        if (assessList.length > 0) setAssessment(assessList[0].code);
        setSubjects((dropdowns.subjects || []).filter(s => s.active !== false));
        setClasses((dropdowns.classes || []).filter(c => c.active !== false));
        setLocations((dropdowns.locations || []).filter(l => l.active !== false));
      } catch (e) { console.error('Error loading system settings', e); }

      try {
        const staffSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'staff_profiles'));
        const list = [];
        staffSnap.forEach(d => {
          const data = d.data();
          if (!data.initial) return;
          list.push({ email: d.id, initial: data.initial, name: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.initial, role: (data.role || 'staff').toLowerCase() });
        });
        list.sort((a, b) => a.initial.localeCompare(b.initial));
        setStaffList(list);
      } catch (e) { console.error('Error loading staff list', e); }
    })();
  }, [activeSchoolId]);

  const classMap = useMemo(() => Object.fromEntries(classes.map(c => [c.code, c])), [classes]);
  const staffMap = useMemo(() => Object.fromEntries(staffList.map(s => [s.initial, s])), [staffList]);
  const classLevels = useMemo(() => [...new Set(classes.map(c => c.level).filter(Boolean))].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })), [classes]);

  // --- Sessions: the framework/schedule/allocation data, all on one doc per session ---
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  useEffect(() => {
    if (!activeSchoolId || !schoolYear || !assessment) { setSessions([]); return; }
    setLoadingSessions(true);
    const q = query(
      collection(db, 'schools', activeSchoolId, 'invigilation_session'),
      where('schoolYear', '==', schoolYear),
      where('assessment', '==', assessment)
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.subject || '').localeCompare(b.subject || '') || (a.class || '').localeCompare(b.class || '', undefined, { numeric: true }));
      setSessions(list);
      setLoadingSessions(false);
    }, (e) => { console.error('Error loading invigilation sessions', e); setLoadingSessions(false); });
    return () => unsub();
  }, [activeSchoolId, schoolYear, assessment]);

  // --- Scheduled sessions, ascending date then time then subject then class -- shared base list for
  // both the Invigilation tab (read-only overview of every allocation) and the Allocation tab (where
  // they're assigned). ---
  const scheduledSessions = useMemo(() => {
    return [...sessions]
      .filter(s => s.date)
      .sort((a, b) => `${a.date} ${a.startTime || ''} ${a.subject || ''} ${a.class || ''}`
        .localeCompare(`${b.date} ${b.startTime || ''} ${b.subject || ''} ${b.class || ''}`, undefined, { numeric: true }));
  }, [sessions]);

  // --- Statistics tab: every session for the current school year, across ALL assessments (not just
  // the one selected above) -- loaded lazily, only while that tab is open, since it's a separate,
  // wider query than the rest of the module needs. ---
  const [statsSessions, setStatsSessions] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);
  useEffect(() => {
    if (activeTab !== 'statistics' || !activeSchoolId || !schoolYear) return;
    setLoadingStats(true);
    const q = query(collection(db, 'schools', activeSchoolId, 'invigilation_session'), where('schoolYear', '==', schoolYear));
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setStatsSessions(list);
      setLoadingStats(false);
    }, (e) => { console.error('Error loading invigilation statistics', e); setLoadingStats(false); });
    return () => unsub();
  }, [activeTab, activeSchoolId, schoolYear]);

  // { [initial]: { [assessmentCode]: totalMinutes } }
  const statsMatrix = useMemo(() => {
    const totals = {};
    statsSessions.forEach(s => {
      (s.invigilators || []).forEach(initial => {
        if (!totals[initial]) totals[initial] = {};
        totals[initial][s.assessment] = (totals[initial][s.assessment] || 0) + (Number(s.duration) || 0);
      });
    });
    return totals;
  }, [statsSessions]);

  // --- Invigilation tab: every allocation grouped into one card per date, ascending -- each card's
  // rows are already time/subject/class ascending since scheduledSessions is sorted that way and a
  // Map preserves each date's first-seen (i.e. ascending) position. ---
  const invigilationByDate = useMemo(() => {
    const groups = new Map();
    scheduledSessions.forEach(s => {
      if (!groups.has(s.date)) groups.set(s.date, []);
      groups.get(s.date).push(s);
    });
    return [...groups.entries()];
  }, [scheduledSessions]);

  const assignInvigilator = async (session, initial) => {
    if (!initial) return;
    const current = session.invigilators || [];
    if (current.includes(initial)) return;
    try {
      await setDoc(doc(db, 'schools', activeSchoolId, 'invigilation_session', session.id), { invigilators: [...current, initial] }, { merge: true });
    } catch (e) {
      console.error('Error assigning invigilator', e);
      alert(t('invigilation.assignFailed', { message: e.message }));
    }
  };
  const removeInvigilator = async (session, initial) => {
    try {
      await setDoc(doc(db, 'schools', activeSchoolId, 'invigilation_session', session.id), { invigilators: (session.invigilators || []).filter(i => i !== initial) }, { merge: true });
    } catch (e) {
      console.error('Error removing invigilator', e);
      alert(t('invigilation.removeFailed', { message: e.message }));
    }
  };

  // Venue is a single value per session (one room, unlike the invigilators list) -- dropping a new
  // one on a session just replaces whatever was there.
  const assignVenue = async (session, code) => {
    if (!code) return;
    try {
      await setDoc(doc(db, 'schools', activeSchoolId, 'invigilation_session', session.id), { venue: code }, { merge: true });
    } catch (e) {
      console.error('Error assigning venue', e);
      alert(t('invigilation.assignVenueFailed', { message: e.message }));
    }
  };
  const removeVenue = async (session) => {
    try {
      await setDoc(doc(db, 'schools', activeSchoolId, 'invigilation_session', session.id), { venue: '' }, { merge: true });
    } catch (e) {
      console.error('Error removing venue', e);
      alert(t('invigilation.removeVenueFailed', { message: e.message }));
    }
  };

  // --- Settings: exam periods (per school year + assessment), who can edit, and the shared "current
  // assessment" every other tab reads instead of each showing its own School Year/Assessment picker ---
  const [examPeriods, setExamPeriods] = useState({});
  const [editPermissions, setEditPermissions] = useState({ roles: [] });
  // Who is eligible to be dragged onto a session as an invigilator. Nothing configured (the default)
  // means every staff member shows in the Staff panel, same as today -- checking a role or adding
  // someone under "Others" narrows it down to just those.
  const [invigilatorEligibility, setInvigilatorEligibility] = useState({ roles: [], staff: [] });
  // Locations & Rooms from System Parameters, minus whichever ones the admin has marked unusable for
  // an exam (e.g. too small, no desks) -- opt-out rather than opt-in, so every active location is a
  // usable exam venue by default and nothing needs configuring until a specific one doesn't work.
  const [venueAvailability, setVenueAvailability] = useState({ unavailable: [] });
  const [currentAssessment, setCurrentAssessment] = useState(null); // { schoolYear, assessment } | null
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Admins always have full access -- a safety net against an accidental misconfiguration locking
  // everyone (including admins) out, same pattern as Attendance's canEditCategory.
  const canManage = isAdmin || isModuleAdmin || editPermissions.roles.includes(ownActorRole);

  // Who actually shows up in the Staff panel to drag onto a session. Nothing checked means everyone
  // is eligible; as soon as a role or a specific "Other" is checked, only those match.
  const eligibleStaffList = useMemo(() => {
    if (invigilatorEligibility.roles.length === 0 && invigilatorEligibility.staff.length === 0) return staffList;
    return staffList.filter(s =>
      invigilatorEligibility.roles.includes(actorRoleBucket(s.role)) || invigilatorEligibility.staff.includes(s.email)
    );
  }, [staffList, invigilatorEligibility]);

  const availableVenues = useMemo(
    () => locations.filter(loc => !venueAvailability.unavailable.includes(loc.code)),
    [locations, venueAvailability]
  );

  // Statistics tab rows: every potential invigilator (per the eligibility settings above) so someone
  // with zero assignments still shows up with "-" cells, plus anyone with an actual recorded
  // assignment even if they're no longer eligible today (their history shouldn't just disappear).
  const statsInvigilators = useMemo(() => {
    const byInitial = new Map();
    eligibleStaffList.forEach(s => byInitial.set(s.initial, s));
    staffList.forEach(s => { if (statsMatrix[s.initial]) byInitial.set(s.initial, s); });
    return [...byInitial.values()].sort((a, b) => a.initial.localeCompare(b.initial));
  }, [eligibleStaffList, staffList, statsMatrix]);

  const [selectedLevel, setSelectedLevel] = useState('');
  const [scheduleDrafts, setScheduleDrafts] = useState({}); // { [sessionId]: { date: string|null, startTime } }
  const [savingSchedule, setSavingSchedule] = useState(false);
  // Collapsible-section state shared by the Schedule tab (keyed "level:<date>") and Allocation tab
  // (keyed "alloc:<level>") so each column can be individually hidden to focus on the rest.
  const [collapsedSections, setCollapsedSections] = useState({});
  const toggleSection = (key) => setCollapsedSections(prev => ({ ...prev, [key]: !prev[key] }));

  const periodKey = `${schoolYear}__${assessment}`;

  useEffect(() => {
    if (!activeSchoolId) return;
    setSettingsLoaded(false);
    getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'invigilation_settings'))
      .then(snap => {
        const data = snap.exists() ? snap.data() : {};
        setExamPeriods(data.examPeriods || {});
        setEditPermissions({ roles: data.editPermissions?.roles || [] });
        setInvigilatorEligibility({
          roles: data.invigilatorEligibility?.roles || [],
          staff: data.invigilatorEligibility?.staff || [],
        });
        setVenueAvailability({ unavailable: data.venueAvailability?.unavailable || [] });
        setCurrentAssessment(data.currentAssessment || null);
      })
      .catch(e => console.error('Error loading invigilation settings', e))
      .finally(() => setSettingsLoaded(true));
  }, [activeSchoolId]);

  // Once both the settings doc and the academic-year/assessment lists have loaded, apply the saved
  // "current assessment" over the cur_year/first-assessment defaults -- re-runs on either finishing
  // last, so it converges correctly regardless of which fetch completes first.
  useEffect(() => {
    if (!settingsLoaded || !currentAssessment) return;
    if (currentAssessment.schoolYear && academicYears.some(y => y.yearId === currentAssessment.schoolYear)) {
      setSchoolYear(currentAssessment.schoolYear);
    }
    if (currentAssessment.assessment && assessments.some(a => a.code === currentAssessment.assessment)) {
      setAssessment(currentAssessment.assessment);
    }
  }, [settingsLoaded, currentAssessment, academicYears, assessments]);

  useEffect(() => {
    if (!classLevels.includes(selectedLevel)) setSelectedLevel(classLevels[0] || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classLevels]);

  useEffect(() => { setScheduleDrafts({}); }, [schoolYear, assessment, selectedLevel]);

  // Read-only outside Settings -- Exam Start/End Date editing now lives in the Settings tab, covering
  // every assessment at once instead of one at a time.
  const periodDraft = useMemo(() => examPeriods[periodKey] || { startDate: '', endDate: '' }, [examPeriods, periodKey]);
  const examDates = useMemo(() => dateRange(periodDraft.startDate, periodDraft.endDate), [periodDraft]);

  // Only the first 5 exam dates are expanded by default on the Schedule tab -- any date beyond that
  // starts collapsed, so a long exam period doesn't dump every day's timetable on screen at once.
  // Only sets a default for dates never explicitly toggled before, so it never fights a manual
  // expand/collapse the user already made.
  useEffect(() => {
    if (examDates.length <= 5) return;
    setCollapsedSections(prev => {
      let changed = false;
      const next = { ...prev };
      examDates.forEach((date, i) => {
        const key = `level:${date}`;
        if (i >= 5 && !(key in next)) { next[key] = true; changed = true; }
      });
      return changed ? next : prev;
    });
  }, [examDates]);

  // Same default on the Allocation tab: only the first 5 class levels are expanded, so a school with
  // many levels doesn't cram every column onto the screen at once -- and the columns that ARE expanded
  // get more room each (see the flex-grow columns in TimetableColumnsRow).
  useEffect(() => {
    if (classLevels.length <= 5) return;
    setCollapsedSections(prev => {
      let changed = false;
      const next = { ...prev };
      classLevels.forEach((level, i) => {
        const key = `alloc:${level}`;
        if (i >= 5 && !(key in next)) { next[key] = true; changed = true; }
      });
      return changed ? next : prev;
    });
  }, [classLevels]);

  // The timetable's visible time range for the current assessment -- falls back to the 08:00-17:00
  // defaults when this assessment hasn't set its own day start/end time in Settings, and falls back
  // again if a bad pair (end <= start) somehow got saved.
  const dayStartMin = timeToMinutes(periodDraft.startTime) ?? TIMETABLE_START_MIN;
  const dayEndMinRaw = timeToMinutes(periodDraft.endTime) ?? TIMETABLE_END_MIN;
  const dayEndMin = dayEndMinRaw > dayStartMin ? dayEndMinRaw : dayStartMin + (TIMETABLE_END_MIN - TIMETABLE_START_MIN);

  // The Schedule tab shows one exam date at a time (Previous/Next Day), so overlapping sessions on
  // that day can be laid out side-by-side in their own lane instead of stacking on top of each other.
  const [selectedScheduleDate, setSelectedScheduleDate] = useState('');
  useEffect(() => {
    if (examDates.length > 0 && !examDates.includes(selectedScheduleDate)) {
      setSelectedScheduleDate(examDates[0]);
    } else if (examDates.length === 0 && selectedScheduleDate) {
      setSelectedScheduleDate('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examDates]);

  // --- Settings tab: draft state for the Current Assessment picker and its Assessment Periods table,
  // committed together (with editPermissions) by one Save Settings action ---
  const [assessmentDraft, setAssessmentDraft] = useState({ schoolYear: '', assessment: '' });
  useEffect(() => {
    setAssessmentDraft(prev => (prev.schoolYear || prev.assessment) ? prev : { schoolYear, assessment });
  }, [schoolYear, assessment]);

  const [periodsDraft, setPeriodsDraft] = useState({}); // { [assessmentCode]: {startDate, endDate, startTime, endTime} }, scoped to assessmentDraft.schoolYear
  useEffect(() => {
    if (!assessmentDraft.schoolYear) return;
    const next = {};
    assessments.forEach(a => {
      next[a.code] = examPeriods[`${assessmentDraft.schoolYear}__${a.code}`] || { startDate: '', endDate: '', startTime: '', endTime: '' };
    });
    setPeriodsDraft(next);
  }, [assessmentDraft.schoolYear, assessments, examPeriods]);

  const toggleEditRole = (role) => {
    setEditPermissions(prev => ({
      ...prev,
      roles: prev.roles.includes(role) ? prev.roles.filter(r => r !== role) : [...prev.roles, role],
    }));
  };

  const toggleInvigilatorRole = (role) => {
    setInvigilatorEligibility(prev => ({
      ...prev,
      roles: prev.roles.includes(role) ? prev.roles.filter(r => r !== role) : [...prev.roles, role],
    }));
  };
  const [showInvigilatorsModal, setShowInvigilatorsModal] = useState(false);
  const [invigilatorsSearch, setInvigilatorsSearch] = useState('');
  const toggleInvigilatorStaff = (email) => {
    setInvigilatorEligibility(prev => ({
      ...prev,
      staff: prev.staff.includes(email) ? prev.staff.filter(e => e !== email) : [...prev.staff, email],
    }));
  };

  const toggleVenueAvailability = (code) => {
    setVenueAvailability(prev => ({
      unavailable: prev.unavailable.includes(code) ? prev.unavailable.filter(c => c !== code) : [...prev.unavailable, code],
    }));
  };

  const handleSaveInvigilationSettings = async () => {
    setSavingSettings(true);
    try {
      // examPeriods is a MAP, and `setDoc(..., { merge: true })` merges maps key by key: it can
      // add one and change one, but it can never remove one. So clearing an assessment's dates did
      // nothing at all -- the old period was still there when the page was reopened. Two faults in
      // one line, both fixed here: the cleared key is now dropped locally, and sent as an explicit
      // deleteField() so the stored map loses it too.
      //
      // merge is kept rather than writing the whole document, because this settings document is
      // shared: a migration writes editPermissions into it, and older schools carry fields nothing
      // reads any more. Replacing it wholesale would quietly take those with it.
      const nextPeriods = { ...examPeriods };
      const periodWrites = {};
      Object.entries(periodsDraft).forEach(([code, p]) => {
        const key = `${assessmentDraft.schoolYear}__${code}`;
        if (p.startDate && p.endDate) {
          nextPeriods[key] = p;
          periodWrites[key] = p;
        } else if (key in nextPeriods) {
          delete nextPeriods[key];
          periodWrites[key] = deleteField();
        }
      });
      await setDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'invigilation_settings'), {
        examPeriods: periodWrites,
        editPermissions,
        invigilatorEligibility,
        venueAvailability,
        currentAssessment: assessmentDraft,
      }, { merge: true });
      setExamPeriods(nextPeriods);
      setCurrentAssessment(assessmentDraft);
      if (assessmentDraft.schoolYear) setSchoolYear(assessmentDraft.schoolYear);
      if (assessmentDraft.assessment) setAssessment(assessmentDraft.assessment);
      alert(t('invigilation.settingsSaved'));
    } catch (e) {
      console.error('Error saving invigilation settings', e);
      alert(t('invigilation.settingsFailed', { message: e.message }));
    }
    setSavingSettings(false);
  };

  const annotatedSessions = useMemo(
    () => sessions.map(s => ({
      ...s,
      _draftDate: scheduleDrafts[s.id] ? scheduleDrafts[s.id].date : (s.date || null),
      _draftStartTime: scheduleDrafts[s.id] ? scheduleDrafts[s.id].startTime : (s.startTime || ''),
    })),
    [sessions, scheduleDrafts]
  );
  const levelSessions = useMemo(
    () => annotatedSessions.filter(s => classMap[s.class]?.level === selectedLevel),
    [annotatedSessions, classMap, selectedLevel]
  );

  // Schedule tab: every exam date's lanes, all for the selected level. Grouped -- since the level is
  // already fixed, classes sharing Subject+Paper+start time collapse onto one card instead of each
  // claiming their own lane.
  const levelModeDayLanes = useMemo(
    () => Object.fromEntries(examDates.map(date => [date, computeDayLanes(levelSessions, date, dayStartMin, true)])),
    [examDates, levelSessions, dayStartMin]
  );
  // Allocation tab: every class level's lanes, all for the selected date. Grouped -- same as the
  // Schedule tab, classes sharing Subject+Paper+start time within a level collapse onto one card.
  const dayModeLevelLanes = useMemo(
    () => Object.fromEntries(classLevels.map(level => [
      level,
      computeDayLanes(annotatedSessions.filter(s => classMap[s.class]?.level === level), selectedScheduleDate, dayStartMin, true),
    ])),
    [classLevels, annotatedSessions, classMap, selectedScheduleDate, dayStartMin]
  );

  // Allocation tab: which (session, invigilator) pairs double-book that person against another
  // session on the same day (any level) whose time range overlaps -- doesn't block the assignment,
  // just flags it so the admin notices and can resolve it. Map: sessionId -> Set of clashing initials.
  const invigilatorClashesByDate = useMemo(() => {
    const daySessions = annotatedSessions.filter(s => s._draftDate === selectedScheduleDate && s._draftStartTime);
    const clashes = new Map();
    const flag = (id, initial) => {
      if (!clashes.has(id)) clashes.set(id, new Set());
      clashes.get(id).add(initial);
    };
    for (let i = 0; i < daySessions.length; i++) {
      const a = daySessions[i];
      const aStart = timeToMinutes(a._draftStartTime);
      const aEnd = aStart + (Number(a.duration) || 60);
      for (let j = i + 1; j < daySessions.length; j++) {
        const b = daySessions[j];
        if (a.id === b.id) continue;
        const bStart = timeToMinutes(b._draftStartTime);
        const bEnd = bStart + (Number(b.duration) || 60);
        if (!(aStart < bEnd && bStart < aEnd)) continue;
        (a.invigilators || []).forEach(initial => {
          if ((b.invigilators || []).includes(initial)) { flag(a.id, initial); flag(b.id, initial); }
        });
      }
    }
    return clashes;
  }, [annotatedSessions, selectedScheduleDate]);
  const isInvigilatorClashing = (sessionId, initial) => invigilatorClashesByDate.get(sessionId)?.has(initial) || false;

  // Auto Assign: fills every scheduled session that has NO invigilator yet, across the whole exam
  // period (not just the day currently shown). Sessions are processed date asc -> time asc -> class
  // asc; for each one, the eligible candidate with the LEAST total invigilation minutes so far (across
  // this exam period, including their pre-existing manual assignments) is picked, so total time ends up
  // balanced across staff rather than just cycling by initial -- ties (equal load) fall back to initial
  // ascending, which keeps the result deterministic. A candidate is skipped for a given session if it
  // would double-book them against ANY session (already-assigned or freshly auto-assigned) whose time
  // overlaps theirs on the same date, so the result is clash-free by construction. Sessions that already
  // have an invigilator are left untouched.
  const [autoAssigning, setAutoAssigning] = useState(false);
  const handleAutoAssignInvigilators = async () => {
    if (eligibleStaffList.length === 0) return alert(t('invigilation.noEligibleStaff'));
    const targets = scheduledSessions.filter(s => (s.invigilators || []).length === 0 && s.startTime);
    if (targets.length === 0) return alert(t('invigilation.allAssigned'));
    if (!window.confirm(t('invigilation.autoAssignConfirm', { count: targets.length }))) return;

    setAutoAssigning(true);
    try {
      // Seed busy time ranges AND running total minutes from every CURRENTLY assigned session
      // (including ones being left alone), so auto-assignment never creates a clash against an
      // existing manual assignment, and balances against time staff already have on the books.
      const busy = {}; // initial -> [{ date, start, end }]
      const totalMinutes = {}; // initial -> minutes
      eligibleStaffList.forEach(st => { totalMinutes[st.initial] = 0; });
      scheduledSessions.forEach(s => {
        const start = timeToMinutes(s.startTime);
        if (start == null) return;
        const duration = Number(s.duration) || 60;
        const end = start + duration;
        (s.invigilators || []).forEach(initial => {
          if (!busy[initial]) busy[initial] = [];
          busy[initial].push({ date: s.date, start, end });
          totalMinutes[initial] = (totalMinutes[initial] || 0) + duration;
        });
      });

      const sortedTargets = [...targets].sort((a, b) =>
        `${a.date} ${a.startTime} ${a.class}`.localeCompare(`${b.date} ${b.startTime} ${b.class}`, undefined, { numeric: true })
      );
      const staffOrder = [...eligibleStaffList].sort((a, b) => a.initial.localeCompare(b.initial));

      const updates = []; // { sessionId, initial }
      const unassigned = [];

      sortedTargets.forEach(session => {
        const start = timeToMinutes(session.startTime);
        const duration = Number(session.duration) || 60;
        const end = start + duration;
        let best = null;
        staffOrder.forEach(candidate => {
          const conflicts = (busy[candidate.initial] || []).some(b => b.date === session.date && start < b.end && b.start < end);
          if (conflicts) return;
          const load = totalMinutes[candidate.initial] || 0;
          if (!best || load < best.load) best = { initial: candidate.initial, load };
        });
        if (best) {
          updates.push({ sessionId: session.id, initial: best.initial });
          if (!busy[best.initial]) busy[best.initial] = [];
          busy[best.initial].push({ date: session.date, start, end });
          totalMinutes[best.initial] = (totalMinutes[best.initial] || 0) + duration;
        } else {
          unassigned.push(session);
        }
      });

      let batch = writeBatch(db);
      let batchCount = 0;
      for (const { sessionId, initial } of updates) {
        batch.set(doc(db, 'schools', activeSchoolId, 'invigilation_session', sessionId), { invigilators: [initial] }, { merge: true });
        batchCount++;
        if (batchCount === 500) { await batch.commit(); batch = writeBatch(db); batchCount = 0; }
      }
      if (batchCount > 0) await batch.commit();

      alert(
        t('invigilation.autoAssignDone', { count: updates.length }) +
        (unassigned.length ? t('invigilation.autoAssignLeftOver', { count: unassigned.length }) : '')
      );
    } catch (e) {
      console.error('Error auto-assigning invigilators', e);
      alert(t('invigilation.autoAssignFailed', { message: e.message }));
    }
    setAutoAssigning(false);
  };

  const handleCardDragStart = (e, sessionId) => { e.dataTransfer.setData('text/plain', sessionId); };
  // Staff chips use a distinct MIME type (not text/plain) so a card's own drop handler can tell a
  // staff-assignment drag apart from a session-repositioning drag and let the latter bubble up as before.
  const handleStaffDragStart = (e, initial) => {
    e.dataTransfer.setData('application/x-staff-initial', initial);
    e.dataTransfer.effectAllowed = 'copy';
  };
  // Venue chips get their own MIME type too, so a card's drop handler can tell "assign this venue"
  // apart from "assign this staff member" apart from "reposition this session".
  const handleVenueDragStart = (e, code) => {
    e.dataTransfer.setData('application/x-venue-code', code);
    e.dataTransfer.effectAllowed = 'copy';
  };
  const handleColumnDragOver = (e) => e.preventDefault();
  // Dropping onto the timetable itself sets the start time from exactly where the card lands (snapped
  // to the nearest 30-minute row), so the drop position is actually what determines the time -- rather
  // than everything landing on a single hardcoded default. Dropping onto the Unscheduled pool (no date)
  // has no position to read, so it just keeps whatever time the session already had, if any.
  const handleColumnDrop = (e, date) => {
    e.preventDefault();
    // A grouped card's session.id is several real session IDs joined by commas (see
    // groupSessionsForCard) -- dragging or retiming it moves every member together.
    const raw = e.dataTransfer.getData('text/plain');
    if (!raw) return;
    const ids = raw.split(',');
    let startTime;
    if (date) {
      const rect = e.currentTarget.getBoundingClientRect();
      const offsetY = e.clientY - rect.top;
      const rawMinutes = dayStartMin + offsetY / PX_PER_MIN;
      const snappedMinutes = Math.round(rawMinutes / TIMETABLE_SLOT_MIN) * TIMETABLE_SLOT_MIN;
      const clampedMinutes = Math.min(Math.max(snappedMinutes, dayStartMin), dayEndMin - TIMETABLE_SLOT_MIN);
      startTime = `${pad2(Math.floor(clampedMinutes / 60))}:${pad2(clampedMinutes % 60)}`;
    } else {
      const session = levelSessions.find(s => s.id === ids[0]);
      startTime = session?._draftStartTime || '';
    }
    setScheduleDrafts(prev => {
      const next = { ...prev };
      ids.forEach(id => { next[id] = { date, startTime }; });
      return next;
    });
  };
  // Fine-tunes a placed session's time from the card's own input, independent of the level currently
  // selected (Allocation's per-level columns can edit sessions outside the Schedule tab's own level).
  // sessionId may be several real IDs joined by commas for a grouped card -- see handleColumnDrop.
  const handleDraftTimeChange = (sessionId, time) => {
    const ids = sessionId.split(',');
    setScheduleDrafts(prev => {
      const next = { ...prev };
      ids.forEach(id => {
        next[id] = { date: prev[id]?.date ?? annotatedSessions.find(s => s.id === id)?._draftDate, startTime: time };
      });
      return next;
    });
  };
  const handleSaveSchedule = async () => {
    const entries = Object.entries(scheduleDrafts);
    if (entries.length === 0) return;
    setSavingSchedule(true);
    try {
      const batch = writeBatch(db);
      entries.forEach(([sessionId, draft]) => {
        const session = sessions.find(s => s.id === sessionId);
        const ref = doc(db, 'schools', activeSchoolId, 'invigilation_session', sessionId);
        if (!draft.date) {
          batch.set(ref, { date: null, startTime: null, endTime: null }, { merge: true });
        } else {
          batch.set(ref, { date: draft.date, startTime: draft.startTime, endTime: addMinutes(draft.startTime, session?.duration) }, { merge: true });
        }
      });
      await batch.commit();
      setScheduleDrafts({});
    } catch (e) {
      console.error('Error saving schedule', e);
      alert(t('invigilation.saveScheduleFailed', { message: e.message }));
    }
    setSavingSchedule(false);
  };

  // --- Session tab: define the exam-session framework (Subject / Class / Duration). Sessions are
  // shown read-only in the table (like OLE Management's Activity tab) -- once a session exists it's
  // edited or removed one at a time through the modal below, not by leaving table cells permanently
  // editable, so a stray click can't silently change a session that's already been scheduled/allocated.
  const emptySessionForm = () => ({ subject: '', class: '', paper: '', duration: 60 });
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [sessionForm, setSessionForm] = useState(emptySessionForm());
  const [savingSession, setSavingSession] = useState(false);

  const openAddSessionModal = () => {
    setEditingSessionId(null);
    setSessionForm(emptySessionForm());
    setShowSessionModal(true);
  };
  const openEditSessionModal = (session) => {
    setEditingSessionId(session.id);
    setSessionForm({ subject: session.subject || '', class: session.class || '', paper: session.paper || '', duration: session.duration || 60 });
    setShowSessionModal(true);
  };

  const handleSaveSessionModal = async (e) => {
    e.preventDefault();
    if (!sessionForm.subject || !sessionForm.class || !sessionForm.duration) return alert(t('invigilation.sessionNeedsFields'));
    setSavingSession(true);
    try {
      const ref = editingSessionId
        ? doc(db, 'schools', activeSchoolId, 'invigilation_session', editingSessionId)
        : doc(collection(db, 'schools', activeSchoolId, 'invigilation_session'));
      await setDoc(ref, {
        schoolYear, assessment,
        subject: sessionForm.subject, class: sessionForm.class, paper: sessionForm.paper || '', duration: Number(sessionForm.duration) || 0,
      }, { merge: true });
      setShowSessionModal(false);
    } catch (e) {
      console.error('Error saving session', e);
      alert(t('invigilation.saveSessionFailed', { message: e.message }));
    }
    setSavingSession(false);
  };

  const handleDeleteSession = async (session) => {
    if (!window.confirm(t('invigilation.deleteSessionConfirm'))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'invigilation_session', session.id));
    } catch (e) {
      console.error('Error deleting session', e);
      alert(t('invigilation.deleteSessionFailed', { message: e.message }));
    }
  };

  // --- Module-wide CSV export/import (Session framework + Schedule + Allocation, all in one file) --
  // lives in the tabs bar rather than one tab, since it covers every session across every school year
  // and assessment, not just whatever's currently filtered. Meant for advanced users who'd rather
  // batch-edit a spreadsheet than click through the GUI for Session/Schedule/Allocation one at a time.
  const invigilationFileInputRef = useRef(null);
  const [importingInvigilation, setImportingInvigilation] = useState(false);
  const [exportingInvigilation, setExportingInvigilation] = useState(false);

  const handleExportInvigilationCSV = async () => {
    setExportingInvigilation(true);
    try {
      const snap = await getDocs(collection(db, 'schools', activeSchoolId, 'invigilation_session'));
      const csvData = [];
      snap.forEach(d => {
        const row = d.data();
        csvData.push({
          'Session ID': d.id,
          'School Year': row.schoolYear || '',
          Assessment: row.assessment || '',
          Subject: row.subject || '',
          Paper: row.paper || '',
          Class: row.class || '',
          Duration: row.duration || 0,
          Date: row.date || '',
          'Start Time': row.startTime || '',
          'End Time': row.endTime || '',
          Venue: row.venue || '',
          Invigilators: (row.invigilators || []).join(';'),
        });
      });
      if (csvData.length === 0) {
        csvData.push({ 'Session ID': '', 'School Year': '', Assessment: '', Subject: '', Paper: '', Class: '', Duration: '', Date: '', 'Start Time': '', 'End Time': '', Venue: '', Invigilators: '' });
      }
      const csv = Papa.unparse(csvData);
      const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'invigilation_sessions_export.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error exporting invigilation data', e);
      alert(t('invigilation.exportFailed', { message: e.message }));
    }
    setExportingInvigilation(false);
  };

  const handleImportInvigilationFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportingInvigilation(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        let added = 0, updated = 0, skipped = 0;
        try {
          // Chunked at 500 ops/commit (Firestore's writeBatch limit) so a large batch upload from an
          // advanced user doesn't just fail outright once the file has more than 500 rows.
          let batch = writeBatch(db);
          let batchCount = 0;
          const commitBatch = async () => {
            if (batchCount === 0) return;
            await batch.commit();
            batch = writeBatch(db);
            batchCount = 0;
          };

          for (const r of (results.data || [])) {
            const sessionId = (r['Session ID'] || '').trim();
            const subject = (r.Subject || '').trim();
            const paper = (r.Paper || '').trim();
            const cls = (r.Class || '').trim();
            const duration = parseInt(r.Duration, 10);
            if (!subject || !cls || !duration) { skipped++; continue; }

            const rowSchoolYear = (r['School Year'] || '').trim() || schoolYear;
            const rowAssessment = (r.Assessment || '').trim() || assessment;
            const payload = { schoolYear: rowSchoolYear, assessment: rowAssessment, subject, paper, class: cls, duration };

            // Date/Start Time/End Time/Venue/Invigilators are only written when the cell is non-empty --
            // a blank cell on an update row means "leave as-is", not "clear it", so a bulk re-export
            // of just the framework columns can't accidentally wipe out someone's schedule/allocation.
            const date = (r.Date || '').trim();
            const startTime = normalizeTime((r['Start Time'] || '').trim());
            if (date) payload.date = date;
            if (startTime) {
              payload.startTime = startTime;
              payload.endTime = normalizeTime((r['End Time'] || '').trim()) || addMinutes(startTime, duration);
            } else if ((r['End Time'] || '').trim()) {
              payload.endTime = normalizeTime(r['End Time'].trim());
            }
            // Venue/Invigilators: unlike Date/Time above, a blank cell here is treated as an explicit
            // clear (not "leave as-is") whenever the column itself is present in the file -- e.g.
            // re-importing an export after blanking out the Invigilators column is the normal way to
            // bulk-unassign. Only a column that's missing from the file entirely (no such header) is
            // left untouched, so a minimal add-only template without a Venue/Invigilators column at
            // all still doesn't wipe anything out.
            if (r.Venue !== undefined) {
              payload.venue = r.Venue.trim();
            }
            if (r.Invigilators !== undefined) {
              const trimmed = r.Invigilators.trim();
              payload.invigilators = trimmed ? trimmed.split(';').map(s => s.trim()).filter(Boolean) : [];
            }

            const ref = sessionId
              ? doc(db, 'schools', activeSchoolId, 'invigilation_session', sessionId)
              : doc(collection(db, 'schools', activeSchoolId, 'invigilation_session'));
            batch.set(ref, payload, { merge: true });
            batchCount++;
            if (sessionId) updated++; else added++;
            if (batchCount === 500) await commitBatch();
          }
          await commitBatch();
          alert(skipped
            ? t('invigilation.importDoneSkipped', { added, updated, skipped })
            : t('invigilation.importDone', { added, updated }));
        } catch (err) {
          console.error('Error importing invigilation data', err);
          alert(t('invigilation.importFailed', { message: err.message }));
        }
        setImportingInvigilation(false);
      },
      error: (err) => { setImportingInvigilation(false); alert(t('invigilation.parseFailed', { message: err.message })); },
    });
  };

  const tabs = [
    { id: 'invigilation', label: t('invigilation.tabs.invigilation'), icon: ListChecks },
    { id: 'allocation', label: t('invigilation.tabs.allocation'), icon: Users },
    { id: 'schedule', label: t('invigilation.tabs.schedule'), icon: CalendarRange },
    { id: 'session', label: t('invigilation.tabs.session'), icon: LayoutList },
    { id: 'statistics', label: t('invigilation.tabs.statistics'), icon: BarChart3 },
    { id: 'settings', label: t('invigilation.tabs.settings'), icon: SettingsIcon },
  ];

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tabs Bar */}
      <div className="theme-tabs-bar" style={{ minHeight: '66px' }}>
        <div className="theme-tabs-group">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`theme-tab ${isActive ? 'active' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                <div className="theme-tab-icon"><Icon size={18} /></div>
                {tab.label}
              </button>
            );
          })}
        </div>

        <HeaderActionsPortal>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {activeTab === 'schedule' && (
            <select className="input-field" value={selectedLevel} onChange={e => setSelectedLevel(e.target.value)} style={{ width: '120px' }}>
              {classLevels.length === 0 ? <option value="">-</option> : classLevels.map(l => <option key={l} value={l}>{t('invigilation.level', { level: l })}</option>)}
            </select>
          )}
          {activeTab === 'allocation' && (
            <select className="input-field" value={selectedScheduleDate} onChange={e => setSelectedScheduleDate(e.target.value)} style={{ width: '150px' }}>
              {examDates.length === 0 ? <option value="">-</option> : examDates.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          )}
          {/* School Year/Assessment are no longer pickable here -- set the shared "current assessment"
              once in the Settings tab instead of every tab showing its own pair of dropdowns. */}
          <div style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
            {(assessments.find(a => a.code === assessment)?.nameEn || assessment || '-')} · {schoolYear || '-'}
          </div>
          {canManage && (
            <>
              <input type="file" ref={invigilationFileInputRef} accept=".csv" style={{ display: 'none' }} onChange={handleImportInvigilationFile} />
              <button className="btn-outline" onClick={() => invigilationFileInputRef.current?.click()} disabled={importingInvigilation} title={t('invigilation.importCsv')} style={{ width: 'auto', padding: '0.6rem', display: 'flex', alignItems: 'center' }}>
                <Upload size={16} />
              </button>
              <button className="btn-outline" onClick={handleExportInvigilationCSV} disabled={exportingInvigilation} title={t('invigilation.exportCsv')} style={{ width: 'auto', padding: '0.6rem', display: 'flex', alignItems: 'center' }}>
                <Download size={16} />
              </button>
            </>
          )}
          {activeTab === 'settings' && (
            <button className="btn-primary" onClick={handleSaveInvigilationSettings} disabled={!canManage || savingSettings} style={{ width: 'auto', padding: '0.5rem 1.25rem' }}>
              {savingSettings ? t('common.saving') : t('common.save')}
            </button>
          )}
          {activeTab === 'session' && canManage && (
            <button className="btn-primary" onClick={openAddSessionModal} style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem' }}>
              <Plus size={16} /> {t('invigilation.add')}
            </button>
          )}
          {activeTab === 'schedule' && canManage && Object.keys(scheduleDrafts).length > 0 && (
            <button className="btn-primary" onClick={handleSaveSchedule} disabled={savingSchedule} style={{ width: 'auto', padding: '0.5rem 1.25rem' }}>
              {savingSchedule ? t('common.saving') : t('invigilation.saveCount', { count: Object.keys(scheduleDrafts).length })}
            </button>
          )}
          {activeTab === 'allocation' && canManage && (
            <button
              className="btn-primary" onClick={handleAutoAssignInvigilators} disabled={autoAssigning}
              title={t('invigilation.autoAssignTitle')}
              style={{ width: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem' }}
            >
              <Wand2 size={16} /> {autoAssigning ? t('invigilation.assigning') : t('invigilation.autoAssign')}
            </button>
          )}
        </div>
        </HeaderActionsPortal>
      </div>

      {/* Main Content -- the scrollbar (when content overflows vertically) sits on this outer,
          unpadded div so it never eats into the inner div's width; the tabs bar has no vertical
          scrollbar of its own, so without this split its content would end up very slightly
          narrower than the tabs bar above it. */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
      <div style={{ padding: '0 1.5rem 1.5rem' }}>

        {activeTab === 'invigilation' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {loadingSessions ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)' }}>{t('common.loading')}</div>
            ) : invigilationByDate.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)' }}>{t('invigilation.nothingScheduled')}</div>
            ) : invigilationByDate.map(([date, rows]) => {
              const key = `invig:${date}`;
              const collapsed = !!collapsedSections[key];
              return (
                <div key={date} className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflow: 'hidden' }}>
                  <button
                    type="button" onClick={() => toggleSection(key)}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '0.75rem 1rem', borderBottom: collapsed ? 'none' : '1px solid var(--color-border)', textAlign: 'left' }}
                  >
                    <ChevronDown size={16} style={{ transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.15s', color: 'var(--color-text-muted)', flexShrink: 0 }} />
                    <span style={{ fontWeight: '700', fontSize: '0.95rem' }}>{date}</span>
                    <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>({rows.length} session{rows.length === 1 ? '' : 's'})</span>
                  </button>
                  {!collapsed && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                          <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                            <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('invigilation.time')}</th>
                            <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('invigilation.subject')}</th>
                            <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('invigilation.className')}</th>
                            <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('invigilation.venue')}</th>
                            <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('invigilation.invigilators')}</th>
                            <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('invigilation.status')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((s, i) => {
                            const expired = new Date(`${s.date}T${s.startTime || '00:00'}`) < new Date();
                            return (
                              <tr key={s.id} style={{ borderBottom: i === rows.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem', opacity: expired ? 0.55 : 1 }}>
                                <td style={{ padding: '0.75rem 1rem' }}>{s.startTime} - {s.endTime}</td>
                                <td style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{s.subject}</td>
                                <td style={{ padding: '0.75rem 1rem' }}>{s.class}</td>
                                <td style={{ padding: '0.75rem 1rem' }}>{s.venue || '-'}</td>
                                <td style={{ padding: '0.75rem 1rem' }}>{(s.invigilators || []).length ? s.invigilators.join(', ') : '-'}</td>
                                <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{expired ? t('invigilation.past') : t('invigilation.upcoming')}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'allocation' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {!canManage && (
              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', padding: '0.75rem 1rem', borderRadius: 'var(--radius-md)' }}>
                Only admins and assigned staff can assign invigilators. This is a read-only view.
              </div>
            )}

            {canManage && (
              <StaffPanel
                staffList={eligibleStaffList} onStaffDragStart={handleStaffDragStart}
                collapsed={!!collapsedSections['panel:staff']} onToggle={() => toggleSection('panel:staff')}
              />
            )}
            {canManage && (
              <VenuePanel
                venues={availableVenues} onVenueDragStart={handleVenueDragStart}
                collapsed={!!collapsedSections['panel:venue']} onToggle={() => toggleSection('panel:venue')}
              />
            )}

            {examDates.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)' }}>{t('invigilation.setPeriodFirst')}</div>
            ) : classLevels.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)' }}>{t('invigilation.noClassLevels')}</div>
            ) : (
              // All class levels side by side as columns, sharing one time ruler, so the whole day's
              // invigilation is visible at a glance while dragging staff onto sessions.
              <TimetableColumnsRow
                entries={classLevels.map(level => ({
                  key: `alloc:${level}`, label: `Level ${level}`,
                  lanes: dayModeLevelLanes[level] || { sessions: [], laneCount: 1 }, dropDate: selectedScheduleDate,
                }))}
                canManage={canManage} assignable={canManage} collapsedSections={collapsedSections} onToggleSection={toggleSection}
                onColumnDragOver={handleColumnDragOver} onColumnDrop={handleColumnDrop} onCardDragStart={handleCardDragStart}
                onDropStaff={(session, initial) => assignInvigilator(session, initial)}
                onRemoveStaff={(session, initial) => removeInvigilator(session, initial)}
                onDropVenue={(session, code) => assignVenue(session, code)}
                onRemoveVenue={(session) => removeVenue(session)}
                isInvigilatorClashing={isInvigilatorClashing}
                onTimeChange={handleDraftTimeChange}
                startMin={dayStartMin} endMin={dayEndMin}
              />
            )}
          </div>
        )}

        {activeTab === 'schedule' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {!canManage && (
              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', padding: '0.75rem 1rem', borderRadius: 'var(--radius-md)' }}>
                Only admins and assigned staff can build the schedule. This is a read-only view.
              </div>
            )}

            {examDates.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)' }}>{t('invigilation.setPeriodSettingsFirst')}</div>
            ) : (
              <>
                <div
                  onDragOver={canManage ? handleColumnDragOver : undefined}
                  onDrop={canManage ? (e) => handleColumnDrop(e, null) : undefined}
                  style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', minHeight: '70px', width: '100%', boxSizing: 'border-box', backgroundColor: 'var(--color-bg-card)', border: '1px dashed var(--color-border)', borderRadius: 'var(--radius-lg)', padding: '0.75rem' }}
                >
                  {levelSessions.filter(s => !s._draftDate).map(s => (
                    <div key={s.id} style={{ width: '160px' }}>
                      <SessionCard session={s} draggable={canManage} onDragStart={handleCardDragStart} />
                    </div>
                  ))}
                </div>

                {/* All exam dates side by side as columns, sharing one time ruler, so the whole period
                    is visible at a glance -- each column collapses to a thin strip so a long exam
                    period can be skimmed and only the day(s) being worked on kept open. */}
                <TimetableColumnsRow
                  entries={examDates.map(date => ({
                    key: `level:${date}`, label: date,
                    lanes: levelModeDayLanes[date] || { sessions: [], laneCount: 1 }, dropDate: date,
                  }))}
                  canManage={canManage} assignable={false} collapsedSections={collapsedSections} onToggleSection={toggleSection}
                  onColumnDragOver={handleColumnDragOver} onColumnDrop={handleColumnDrop} onCardDragStart={handleCardDragStart}
                  onTimeChange={handleDraftTimeChange}
                  startMin={dayStartMin} endMin={dayEndMin}
                />
              </>
            )}
          </div>
        )}

        {activeTab === 'session' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {!canManage && (
              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', padding: '0.75rem 1rem', borderRadius: 'var(--radius-md)' }}>
                Only admins and assigned staff can define sessions. This is a read-only view.
              </div>
            )}
            <div className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('invigilation.subject')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('invigilation.paper')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('invigilation.className')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('invigilation.durationMin')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('invigilation.scheduled')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('invigilation.venue')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('invigilation.invigilators')}</th>
                    {canManage && <th style={{ padding: '0.75rem 1rem', fontWeight: '600', width: '80px' }}>{t('invigilation.actions')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {sessions.length === 0 ? (
                    <tr><td colSpan={canManage ? 8 : 7} style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--color-text-muted)' }}>{t('invigilation.noSessionsDefined')}</td></tr>
                  ) : sessions.map((row, i) => (
                    <tr key={row.id} style={{ borderBottom: i === sessions.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                      <td style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{row.subject}</td>
                      <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{row.paper || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{row.class}</td>
                      <td style={{ padding: '0.75rem 1rem' }}>{row.duration || 0}</td>
                      <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{row.date ? `${row.date} ${row.startTime || ''}` : '-'}</td>
                      <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{row.venue || '-'}</td>
                      <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{(row.invigilators || []).length ? row.invigilators.join(', ') : '-'}</td>
                      {canManage && (
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button type="button" onClick={() => openEditSessionModal(row)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', display: 'flex' }} title={t('common.edit')}>
                              <Pencil size={16} />
                            </button>
                            <button type="button" onClick={() => handleDeleteSession(row)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }} title={t('common.delete')}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'statistics' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('invigilation.invigilator')}</th>
                    {assessments.map(a => (
                      <th key={a.code} style={{ padding: '0.75rem 1rem', fontWeight: '600', textAlign: 'right' }}>{a.nameEn || a.code} (min)</th>
                    ))}
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '700', textAlign: 'right' }}>{t('invigilation.totalMin')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingStats ? (
                    <tr><td colSpan={assessments.length + 2} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('common.loading')}</td></tr>
                  ) : statsInvigilators.length === 0 ? (
                    <tr><td colSpan={assessments.length + 2} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>No invigilation time recorded yet for {schoolYear || 'this school year'}.</td></tr>
                  ) : statsInvigilators.map((st, i) => {
                    const rowTotal = assessments.reduce((sum, a) => sum + (statsMatrix[st.initial]?.[a.code] || 0), 0);
                    return (
                      <tr key={st.initial} style={{ borderBottom: i === statsInvigilators.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{st.initial}</td>
                        {assessments.map(a => (
                          <td key={a.code} style={{ padding: '0.75rem 1rem', textAlign: 'right', color: 'var(--color-text-muted)' }}>
                            {(statsMatrix[st.initial]?.[a.code] || 0) || '-'}
                          </td>
                        ))}
                        <td style={{ padding: '0.75rem 1rem', textAlign: 'right', fontWeight: '700' }}>{rowTotal || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
                {statsInvigilators.length > 0 && (
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--color-border)', backgroundColor: 'var(--color-bg-app)', fontSize: '0.875rem', fontWeight: '700' }}>
                      <td style={{ padding: '0.75rem 1rem' }}>{t('invigilation.total')}</td>
                      {assessments.map(a => {
                        const colTotal = statsInvigilators.reduce((sum, st) => sum + (statsMatrix[st.initial]?.[a.code] || 0), 0);
                        return <td key={a.code} style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>{colTotal || '-'}</td>;
                      })}
                      <td style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>
                        {statsInvigilators.reduce((sum, st) => sum + assessments.reduce((s2, a) => s2 + (statsMatrix[st.initial]?.[a.code] || 0), 0), 0) || '-'}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%' }}>
            {!canManage && (
              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', padding: '0.75rem 1rem', borderRadius: 'var(--radius-md)' }}>
                Only admins and assigned staff can change these settings. This is a read-only view.
              </div>
            )}

            <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 1rem' }}>{t('invigilation.currentAssessment')}</h3>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('invigilation.schoolYear')}</label>
                  <select className="input-field" value={assessmentDraft.schoolYear} disabled={!canManage} onChange={e => setAssessmentDraft(prev => ({ ...prev, schoolYear: e.target.value }))} style={{ width: '160px' }}>
                    {academicYears.length === 0 ? <option value="">{t('invigilation.noYears')}</option> : academicYears.map(y => <option key={y.uid} value={y.yearId}>{y.yearId}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('invigilation.assessment')}</label>
                  <select className="input-field" value={assessmentDraft.assessment} disabled={!canManage} onChange={e => setAssessmentDraft(prev => ({ ...prev, assessment: e.target.value }))} style={{ width: '160px' }}>
                    {assessments.length === 0 ? <option value="">{t('invigilation.noAssessments')}</option> : assessments.map(a => <option key={a.code} value={a.code}>{a.nameEn || a.code}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 1rem' }}>{t('invigilation.assessmentPeriods')}</h3>
              {assessments.length === 0 ? (
                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{t('invigilation.noAssessmentsConfigured')}</div>
              ) : (
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'left' }}>
                      <th style={{ padding: '0.5rem 0.75rem' }}>{t('invigilation.assessment')}</th>
                      <th style={{ padding: '0.5rem 0.75rem' }}>{t('invigilation.startDate')}</th>
                      <th style={{ padding: '0.5rem 0.75rem' }}>{t('invigilation.endDate')}</th>
                      <th style={{ padding: '0.5rem 0.75rem' }}>{t('invigilation.startTime')}</th>
                      <th style={{ padding: '0.5rem 0.75rem' }}>{t('invigilation.endTime')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assessments.map(a => (
                      <tr key={a.code} style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{a.nameEn || a.code}</td>
                        <td style={{ padding: '0.6rem 0.75rem' }}>
                          <input
                            type="date" className="input-field" disabled={!canManage}
                            value={periodsDraft[a.code]?.startDate || ''}
                            onChange={e => setPeriodsDraft(prev => ({ ...prev, [a.code]: { ...prev[a.code], startDate: e.target.value } }))}
                            style={{ width: '150px' }}
                          />
                        </td>
                        <td style={{ padding: '0.6rem 0.75rem' }}>
                          <input
                            type="date" className="input-field" disabled={!canManage}
                            value={periodsDraft[a.code]?.endDate || ''}
                            onChange={e => setPeriodsDraft(prev => ({ ...prev, [a.code]: { ...prev[a.code], endDate: e.target.value } }))}
                            style={{ width: '150px' }}
                          />
                        </td>
                        <td style={{ padding: '0.6rem 0.75rem' }}>
                          <input
                            type="time" className="input-field" disabled={!canManage}
                            value={periodsDraft[a.code]?.startTime || ''}
                            onChange={e => setPeriodsDraft(prev => ({ ...prev, [a.code]: { ...prev[a.code], startTime: e.target.value } }))}
                            style={{ width: '120px' }}
                          />
                        </td>
                        <td style={{ padding: '0.6rem 0.75rem' }}>
                          <input
                            type="time" className="input-field" disabled={!canManage}
                            value={periodsDraft[a.code]?.endTime || ''}
                            onChange={e => setPeriodsDraft(prev => ({ ...prev, [a.code]: { ...prev[a.code], endTime: e.target.value } }))}
                            style={{ width: '120px' }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
            <div style={{ flex: '1 1 50%', minWidth: 0, backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 1rem' }}>{t('invigilation.moduleAdmin')}</h3>
              <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: '320px' }}>
                <thead>
                  <tr style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem 0.75rem' }}>{t('invigilation.role')}</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>{t('invigilation.canEdit')}</th>
                  </tr>
                </thead>
                <tbody>
                  {['Admin', 'Teacher', 'Staff'].map(role => (
                    <tr key={role} style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{role}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={role === 'Admin' || editPermissions.roles.includes(role)}
                          disabled={role === 'Admin' || !canManage}
                          onChange={() => toggleEditRole(role)}
                          style={{ width: '16px', height: '16px', cursor: role === 'Admin' ? 'default' : 'pointer' }}
                        />
                      </td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{t('invigilation.others')}</td>
                    <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                        {t('invigilation.namedInAccessControl')}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ flex: '1 1 50%', minWidth: 0, backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 1rem' }}>{t('invigilation.invigilatorsTitle')}</h3>
              <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: '320px' }}>
                <thead>
                  <tr style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem 0.75rem' }}>{t('invigilation.role')}</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>{t('invigilation.eligible')}</th>
                  </tr>
                </thead>
                <tbody>
                  {['Admin', 'Teacher', 'Staff'].map(role => (
                    <tr key={role} style={{ borderTop: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{role}</td>
                      <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={invigilatorEligibility.roles.includes(role)}
                          disabled={!canManage}
                          onChange={() => toggleInvigilatorRole(role)}
                          style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                        />
                      </td>
                    </tr>
                  ))}
                  <tr style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{t('invigilation.others')}</td>
                    <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                      <button
                        type="button" className="btn-outline" disabled={!canManage}
                        onClick={() => { setInvigilatorsSearch(''); setShowInvigilatorsModal(true); }}
                        style={{ width: 'auto', padding: '0.3rem 0.75rem', fontSize: '0.8rem' }}
                      >
                        {invigilatorEligibility.staff.length === 0
                          ? 'None'
                          : invigilatorEligibility.staff.map(email => staffList.find(s => s.email === email)?.initial || email).join(', ')}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            </div>

            <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 1rem' }}>{t('invigilation.venuesTitle')}</h3>
              {locations.length === 0 ? (
                <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>{t('invigilation.noLocations')}</div>
              ) : (
                <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: '320px' }}>
                  <thead>
                    <tr style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', textAlign: 'left' }}>
                      <th style={{ padding: '0.5rem 0.75rem' }}>{t('invigilation.location')}</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>{t('invigilation.available')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations.map(loc => (
                      <tr key={loc.code} style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '0.6rem 0.75rem', fontWeight: '600' }}>{loc.code}</td>
                        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={!venueAvailability.unavailable.includes(loc.code)}
                            disabled={!canManage}
                            onChange={() => toggleVenueAvailability(loc.code)}
                            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Manage "Others" invigilator-eligible staff modal */}
      {showInvigilatorsModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '420px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{t('invigilation.othersModalTitle')}</h3>
              <button type="button" onClick={() => setShowInvigilatorsModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>
            <input
              type="text" className="input-field" placeholder={t('invigilation.searchStaff')} value={invigilatorsSearch}
              onChange={e => setInvigilatorsSearch(e.target.value)}
              style={{ marginBottom: '1rem' }}
            />
            <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {staffList.filter(s => `${s.name} ${s.initial}`.toLowerCase().includes(invigilatorsSearch.toLowerCase())).map(s => {
                const checked = invigilatorEligibility.staff.includes(s.email);
                return (
                  <label key={s.email} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 0.5rem', borderRadius: 'var(--radius-md)', cursor: 'pointer', backgroundColor: checked ? 'var(--color-bg-app)' : 'transparent' }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleInvigilatorStaff(s.email)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                    <span style={{ fontSize: '0.875rem' }}>{s.name} ({s.initial})</span>
                  </label>
                );
              })}
            </div>
            <button type="button" className="btn-primary" style={{ marginTop: '1rem' }} onClick={() => setShowInvigilatorsModal(false)}>{t('invigilation.done')}</button>
          </div>
        </div>
      )}

      {/* Manage "Others" editor staff modal */}
      
      {/* Add / Edit Session Modal */}
      {showSessionModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '420px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{editingSessionId ? 'Edit' : 'Add'} Session</h3>
              <button type="button" onClick={() => setShowSessionModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSaveSessionModal} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('invigilation.subject')}</label>
                <select className="input-field" value={sessionForm.subject} onChange={e => setSessionForm(prev => ({ ...prev, subject: e.target.value }))}>
                  <option value="">{t('invigilation.selectSubject')}</option>
                  {subjects.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('invigilation.paper')}</label>
                <select className="input-field" value={sessionForm.paper} onChange={e => setSessionForm(prev => ({ ...prev, paper: e.target.value }))}>
                  <option value="">{t('invigilation.noPaper')}</option>
                  {['1', '2', '3', '4', '5', '6'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('invigilation.className')}</label>
                <select className="input-field" value={sessionForm.class} onChange={e => setSessionForm(prev => ({ ...prev, class: e.target.value }))}>
                  <option value="">{t('invigilation.selectClass')}</option>
                  {classes.map(c => <option key={c.code} value={c.code}>{c.nameEn || c.code}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('invigilation.durationMinutes')}</label>
                <input type="number" className="input-field" value={sessionForm.duration} onChange={e => setSessionForm(prev => ({ ...prev, duration: e.target.value }))} required />
              </div>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowSessionModal(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }} disabled={savingSession}>{savingSession ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invigilation;
